import { useState } from 'react'
import { AnimatedPoints } from '../../components/v3/AnimatedPoints'
import { formatPoints } from '../../components/v3/points-format'
import { RewardCard, type RewardOption } from '../../components/v3/RewardCard'
import badgerLogo from '../../assets/v3/badger-logo.svg'
import chevronDown from '../../assets/v3/chevron-down.svg'
import warningTriangle from '../../assets/v3/warning-triangle.svg'
import { t } from '../../i18n'

export type CustomerTabKey = 'profile' | 'history' | 'tools'

export interface LinkedCustomer {
  name: string
  /** Settled balance now. The counter animates between renders of this value. */
  points: number
  /** What the open sale will add once it is paid for. Null when nothing is ringing up. */
  pointsAfterPayment?: number | null
  clientSince?: string | null
  visits?: number | null
  rewardsClaimed?: number | null
  /** A customer identified at the till who has not completed sign-up. Their points are
   * held, but they cannot see them until they do. */
  needsSignUp?: boolean
}

/** Figma 2641:7755 — the linked-customer screen. The heart of the till flow.
 *
 * Full-bleed brand blue, unlike the scan screen's two panes: once a customer is linked
 * there is nothing left to scan, so the camera pane gives up its half of the screen.
 */
export function CustomerScreen({
  customer,
  rewards,
  tab,
  onTabChange,
  onUnlink,
  onDone,
  onUseReward,
}: {
  customer: LinkedCustomer
  rewards: RewardOption[]
  tab: CustomerTabKey
  onTabChange: (tab: CustomerTabKey) => void
  onUnlink: () => void
  onDone: () => void
  onUseReward: (reward: RewardOption) => void
}) {
  // "press toggle then list opens, 'After Payment' becomes 'Total'" — the designer's
  // note on the Total Points section.
  const [showingTotal, setShowingTotal] = useState(false)
  const afterPayment = customer.pointsAfterPayment ?? 0
  const total = customer.points + afterPayment

  return (
    <div className="relative size-full overflow-clip rounded-[24px] bg-brand">
      <div className="pointer-events-none absolute left-0 top-0 h-[139px] w-full bg-gradient-to-b from-black/40 to-transparent to-[70.588%]" />

      <button
        type="button"
        onClick={onDone}
        aria-label={t('close')}
        className="absolute left-[50px] top-[25px] grid size-[52px] place-items-center rounded-full bg-white/20 text-[24px] font-medium text-white"
      >
        ✕
      </button>
      <img
        src={badgerLogo}
        alt="Badger"
        className="pointer-events-none absolute left-[1049px] top-[22px] h-[29px] w-[105px]"
      />

      <nav className="absolute left-[45px] top-[115px] flex items-end gap-[19px]">
        {(['profile', 'history', 'tools'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onTabChange(key)}
            aria-pressed={tab === key}
            className={
              tab === key
                ? 'rounded-[31px] border-2 border-brand bg-white px-[27px] py-[10px] text-[18px] font-medium text-brand'
                : 'rounded-[31px] border border-on-brand px-[27px] py-[10px] text-[18px] text-on-brand'
            }
          >
            {t(`v3Tab_${key}` as 'v3Tab_profile')}
          </button>
        ))}
      </nav>

      <button
        type="button"
        onClick={onUnlink}
        className="absolute left-[924px] top-[124px] rounded-[31px] border border-on-brand px-[27px] py-[10px] text-[18px] text-on-brand"
      >
        ← {t('v3UnlinkCustomer')}
      </button>

      <p className="absolute left-[45px] top-[192px] whitespace-nowrap text-[17px] text-on-brand">
        {t('v3LinkedCustomer')}
      </p>

      {/* Identity and balance, side by side on white so they read first. */}
      <div className="absolute left-[45px] top-[231px] flex h-[123px] w-[371px] items-center rounded-[24px] bg-white p-[10px]">
        <div className="flex w-[336px] flex-col items-start">
          <span className="text-[18px] text-on-brand-faint">{t('v3Username')}</span>
          <span className="truncate text-[40px] font-semibold text-brand">{customer.name}</span>
        </div>
      </div>

      <div className="absolute left-[438px] top-[231px] flex h-[123px] w-[249px] items-center rounded-[24px] bg-white p-[10px]">
        <div className="flex h-full flex-1 flex-col items-start gap-[8px] px-[15px] py-[16px]">
          <span className="whitespace-nowrap text-[18px] text-on-brand-faint">
            {showingTotal ? t('v3Total') : t('v3PointsAvailable')}
          </span>
          <AnimatedPoints
            value={showingTotal ? total : customer.points}
            className="text-[40px] font-semibold text-brand"
          />
        </div>
      </div>

      {/* The toggle tucks under the balance card and, per the note, relabels it. */}
      {customer.pointsAfterPayment != null ? (
        <button
          type="button"
          onClick={() => setShowingTotal((open) => !open)}
          aria-expanded={showingTotal}
          className="absolute left-[438px] top-[314px] flex w-[249px] flex-col items-center gap-[10px] rounded-[24px] bg-scan px-[25px] pb-[6px] pt-[60px]"
        >
          <span className="flex w-full items-start gap-[14px] text-[16px] leading-[20px] text-row">
            <span className="min-w-0 flex-1 text-left font-medium">
              {showingTotal ? t('v3Total') : t('v3AfterPayment')}
            </span>
            <span className="whitespace-nowrap font-semibold">
              {showingTotal ? formatPoints(total) : `+${formatPoints(afterPayment)}`} {t('points')}
            </span>
          </span>
          <img
            src={chevronDown}
            alt=""
            className={`size-[24px] transition-transform ${showingTotal ? 'rotate-180' : ''}`}
          />
        </button>
      ) : null}

      <section className="absolute left-[45px] top-[370px] flex h-[170px] w-[371px] items-center rounded-[24px] bg-scan py-[16px] pl-[19px] pr-[10px]">
        <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-[12px] px-[5px]">
          <p className="w-full text-[18px] text-on-brand-soft">{t('v3CustomerInfo')}</p>
          <InfoRow label={t('v3ClientSince')} value={customer.clientSince} />
          <InfoRow label={t('visits')} value={customer.visits} />
          <InfoRow label={t('v3RewardsClaimed')} value={customer.rewardsClaimed} />
        </div>
      </section>

      {customer.needsSignUp ? (
        <div className="absolute left-[45px] top-[556px] flex w-[371px] items-center rounded-[24px] bg-scan/30 p-[24px]">
          <div className="flex min-w-0 flex-1 items-center justify-between px-[5px]">
            <span className="text-[16px] leading-[20px] text-on-brand-soft">
              {t('v3NeedsSignUp')}
            </span>
            <img src={warningTriangle} alt="" className="size-[18px] shrink-0" />
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={onDone}
        className="absolute left-[50px] top-[735px] h-[49px] w-[366px] rounded-[40px] border border-on-brand bg-scan text-[18px] text-white"
      >
        {t('done')}
      </button>

      <section className="absolute left-[709px] top-[231px] flex w-[440px] flex-col rounded-[24px] bg-scan pb-[20px] pl-[19px] pr-[10px] pt-[26px]">
        <p className="mb-[16px] w-[399px] text-[18px] text-white">{t('availableRewards')}</p>
        {/* Bounded height so a long catalogue scrolls inside the panel instead of
         * pushing the Done button off a fixed-height till screen. */}
        <div className="flex max-h-[520px] w-[399px] flex-col gap-[18px] overflow-y-auto">
          {rewards.map((reward) => (
            <RewardCard key={reward.id} reward={reward} onUse={onUseReward} />
          ))}
        </div>
      </section>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null) return null
  return (
    <div className="flex w-full items-start gap-[14px] text-[16px] leading-[20px] text-row">
      <span className="min-w-0 flex-1">{label}</span>
      <span className="whitespace-nowrap font-semibold">{value}</span>
    </div>
  )
}
